import { readingCandidateSchema } from "@bindery/orientation/contracts";
import type { Recommendation } from "@bindery/shared/reader/daily-reading";
import * as z from "zod";
import { readMeta, writeMeta, type ReaderDb } from "@/storage/db";

/** A pool older than this is refetched when the library opens. */
export const POOL_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export const recommendationPoolKey = (readerId: string): string =>
  `recommendationPool.v1:${readerId}`;

const cachedPoolSchema = z.object({
  fetchedAt: z.number(),
  candidates: z.array(readingCandidateSchema),
});

export interface CachedPool {
  fetchedAt: number;
  candidates: Recommendation[];
}

export async function readCachedPool(
  db: ReaderDb,
  readerId: string,
): Promise<CachedPool | undefined> {
  const parsed = cachedPoolSchema.safeParse(
    await readMeta<unknown>(db, recommendationPoolKey(readerId)),
  );
  return parsed.success ? parsed.data : undefined;
}

const poolGenerations = new Map<string, number>();

export const poolGeneration = (readerId: string): number =>
  poolGenerations.get(readerId) ?? 0;

/**
 * Makes every load of this reader's pool that started earlier skip its
 * cache write, so a fetch still in flight cannot recreate a pool that
 * forgetting the reader just deleted.
 */
export function invalidatePoolLoads(readerId: string): void {
  poolGenerations.set(readerId, poolGeneration(readerId) + 1);
}

export function writeCachedPool(
  db: ReaderDb,
  readerId: string,
  pool: CachedPool,
): Promise<void> {
  return writeMeta(db, recommendationPoolKey(readerId), pool);
}

/**
 * A day old, or holding a reading the reader has since finished: finishing
 * one means the reader's words moved and the pool has a gap to refill.
 */
export function isPoolStale(
  pool: CachedPool,
  completedReadingIds: ReadonlySet<string>,
  now: number,
): boolean {
  return (
    now - pool.fetchedAt > POOL_MAX_AGE_MS ||
    pool.candidates.some((candidate) => completedReadingIds.has(candidate.id))
  );
}

export function unreadCandidates(
  candidates: readonly Recommendation[],
  completedReadingIds: ReadonlySet<string>,
): Recommendation[] {
  return candidates.filter(
    (candidate) => !completedReadingIds.has(candidate.id),
  );
}
